/**
 * Utilitaires de déchiffrement pour les paramètres URL chiffrés
 * Utilise crypto-js pour déchiffrer les données avec AES-256-CBC
 *
 * Format PHP : base64url(IV_16bytes + base64(ciphertext))
 * La clé est utilisée directement comme string UTF-8
 */

import CryptoJS from 'crypto-js';

/**
 * Déchiffre une chaîne de caractères encodée en AES-256-CBC
 *
 * @param {string} encryptedData - Données chiffrées en base64 URL-safe
 * @returns {string|null} - Données déchiffrées ou null en cas d'erreur
 */
export function decryptData(encryptedData) {
    try {
        const key = process.env.REACT_APP_ENCRYPTION_KEY;

        if (!key) {
            console.error('REACT_APP_ENCRYPTION_KEY non définie dans .env');
            return null;
        }

        // Décode depuis base64 URL-safe vers base64 standard
        const base64 = encryptedData.replace(/-/g, '+').replace(/_/g, '/');

        // Décode le base64 pour obtenir les bytes bruts
        const rawData = CryptoJS.enc.Base64.parse(base64);

        // Convertit en array de bytes pour extraire IV et ciphertext
        const rawBytes = [];
        for (let i = 0; i < rawData.sigBytes; i++) {
            rawBytes.push((rawData.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff);
        }

        // L'IV fait 16 bytes (premiers 16 bytes)
        const ivBytes = rawBytes.slice(0, 16);
        const iv = CryptoJS.lib.WordArray.create(new Uint8Array(ivBytes));

        // Le reste est le ciphertext en base64 (string ASCII)
        const ciphertextBase64Bytes = rawBytes.slice(16);
        const ciphertextBase64 = String.fromCharCode(...ciphertextBase64Bytes);

        // La clé est utilisée directement comme string UTF-8 (comme en PHP)
        // PHP OpenSSL tronque automatiquement à 32 bytes pour AES-256
        const key32 = key.substring(0, 32);
        const keyWordArray = CryptoJS.enc.Utf8.parse(key32);

        // Déchiffre - le ciphertext est déjà en base64, donc on crée un CipherParams
        const decrypted = CryptoJS.AES.decrypt(
            ciphertextBase64,  // crypto-js accepte directement le base64 string
            keyWordArray,
            {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        );

        // Convertit en string UTF-8
        const result = decrypted.toString(CryptoJS.enc.Utf8);

        if (!result) {
            console.error('Déchiffrement a retourné une chaîne vide');
            return null;
        }

        return result;
    } catch (error) {
        console.error('Erreur de déchiffrement:', error);
        return null;
    }
}

/**
 * Extrait et déchiffre les paramètres de l'URL
 *
 * @returns {Object|null} - Objet avec {id, password} ou null en cas d'erreur
 */
export function getDecryptedParamsFromURL() {
    try {
        // Récupère les paramètres de l'URL
        const urlParams = new URLSearchParams(window.location.search);
        const encryptedId = urlParams.get('a');
        const encryptedPw = urlParams.get('b');

        if (!encryptedId || !encryptedPw) {
            // Pas de paramètres chiffrés dans l'URL (peut être normal si reconnexion via localStorage)
            return null;
        }

        // Déchiffre les valeurs
        const id = decryptData(encryptedId);
        const password = decryptData(encryptedPw);

        if (!id || !password) {
            console.error('Échec du déchiffrement des paramètres');
            return null;
        }

        return {
            id,
            password
        };
    } catch (error) {
        console.error('Erreur lors de la récupération des paramètres:', error);
        return null;
    }
}
